export type ElAttrs = Record<string, unknown>;
export type ElChild = Node | string | number | null | undefined;

/** Small hyperscript-style DOM builder used throughout the sidebar. */
export function el(
  tag: string,
  attrs: ElAttrs = {},
  children: ElChild | ElChild[] = []
): HTMLElement {
  const e = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (k === 'class') e.className = v as string;
    else if (k === 'style') Object.assign(e.style, v as Partial<CSSStyleDeclaration>);
    else if (k.startsWith('on') && typeof v === 'function')
      e.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else e.setAttribute(k, String(v));
  });
  (Array.isArray(children) ? children : [children]).forEach((c) => {
    if (c === null || c === undefined) return;
    e.appendChild(
      typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c
    );
  });
  return e;
}

export function numToAlpha(n: number): string {
  let s = '';
  let num = n - 1;
  do {
    s = String.fromCharCode(65 + (num % 26)) + s;
    num = Math.floor(num / 26) - 1;
  } while (num >= 0);
  return s;
}

export function setBadge(node: HTMLElement, ok: boolean): void {
  node.textContent = ok ? 'OK' : 'NG';
  node.className = `badge ${ok ? 'ok' : 'danger'}`;
}

const collapsedCards = new Set(['shadow', 'slope', 'parking']);
/** Wraps a rendered card section with collapse/expand-on-click behavior, re-rendering the sidebar on toggle. */
export function makeCollapsible(
  sectionEl: HTMLElement,
  cardId: string,
  onToggle: () => void
): HTMLElement {
  const h2 = sectionEl.querySelector('h2');
  if (!h2) return sectionEl;
  const bodyChildren = Array.from(sectionEl.children).filter((c) => c !== h2);
  const isCollapsed = collapsedCards.has(cardId);
  bodyChildren.forEach((c) => {
    (c as HTMLElement).style.display = isCollapsed ? 'none' : '';
  });
  const indicator = el(
    'span',
    { style: { fontSize: '10px', color: 'var(--sub)', fontWeight: 400 } },
    isCollapsed ? '▶' : '▼'
  );
  h2.appendChild(indicator);
  (h2 as HTMLElement).style.cursor = 'pointer';
  h2.addEventListener('click', () => {
    if (collapsedCards.has(cardId)) collapsedCards.delete(cardId);
    else collapsedCards.add(cardId);
    onToggle();
  });
  return sectionEl;
}
